import React from 'react'
import { Box, Typography, LinearProgress, Card, CardContent, Tooltip } from '@mui/material'
import { Activity, AlertCircle, CheckCircle } from 'lucide-react'
import { useGetYouTubeQuotaUsageQuery } from 'graphql/generated/graphql'
import Spinner from 'components/Spinner/Spinner'

interface QuotaUsageContainerProps {
  mediaAccountId: number
}

export default function YouTubeQuotaUsage({ mediaAccountId }: QuotaUsageContainerProps) {
  const { data, loading } = useGetYouTubeQuotaUsageQuery({
    variables: { mediaAccountId },
    pollInterval: 60000, // Refresh every minute
    fetchPolicy: 'network-only',
  })

  if (loading) {
    return (
      <Card
        sx={{
          background: 'rgba(30, 30, 30, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
        }}
      >
        <CardContent sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </CardContent>
      </Card>
    )
  }

  if (!data?.getYouTubeQuotaUsage) {
    return null
  }

  const { used, limit, percentage } = data.getYouTubeQuotaUsage

  return <QuotaUsageDisplay used={used} limit={limit} percentage={percentage} />
}

interface QuotaUsageProps {
  used: number
  limit: number
  percentage: number
}

function QuotaUsageDisplay({ used, limit, percentage }: QuotaUsageProps) {
  const getStatusColor = () => {
    if (percentage >= 90) return '#ef4444' // Red
    if (percentage >= 70) return '#f59e0b' // Orange
    return '#22c55e' // Green
  }

  const getStatusIcon = () => {
    if (percentage >= 90) return <AlertCircle size={20} />
    return <CheckCircle size={20} />
  }

  const getStatusText = () => {
    if (percentage >= 90) return 'High Usage'
    if (percentage >= 70) return 'Moderate Usage'
    return 'Healthy'
  }

  return (
    <Card
      sx={{
        background: 'rgba(30, 30, 30, 0.5)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${getStatusColor()}22 0%, ${getStatusColor()}44 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: getStatusColor(),
            }}
          >
            <Activity size={20} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}
            >
              Daily API Quota
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {getStatusIcon()}
              <Typography
                variant="body2"
                sx={{ color: getStatusColor(), fontWeight: 600 }}
              >
                {getStatusText()}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {used.toLocaleString()} / {limit.toLocaleString()} units
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: getStatusColor(),
                fontWeight: 600,
              }}
            >
              {percentage.toFixed(1)}%
            </Typography>
          </Box>

          <Tooltip
            title={`You're using ${used.toLocaleString()} of your ${limit.toLocaleString()} daily quota. Resets at midnight UTC.`}
            arrow
          >
            <LinearProgress
              variant="determinate"
              value={Math.min(percentage, 100)}
              sx={{
                height: 8,
                borderRadius: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: '4px',
                  background: `linear-gradient(90deg, ${getStatusColor()} 0%, ${getStatusColor()}dd 100%)`,
                },
              }}
            />
          </Tooltip>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            💡 <strong>Tip:</strong> Each comment check uses 1 unit, each reply uses 50 units.
            {percentage >= 70 && ' Consider reducing automation frequency if quota is running low.'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}